import { Layout } from './types'
import { UseAssetFileBrowserReturn } from './useAssetFileBrowser'
import Loading from '@/components/shared/Loading'
import RdaLoading from '@/custolink-views/asset_frontend/redesigns/RdaLoading'
import { useTranslation } from 'react-i18next'
import useUiContext from '@/custolink-hooks/useUiContext'
import { PrimaryButton, SecondaryButton } from '@/custolink-views/asset_frontend/redesigns/formKit'
import { useAssetFrontendDesign } from '@/custolink-views/asset_frontend/redesigns/assetFrontendDesign'
import {
  PiArrowLeft,
  PiBookOpen,
  PiCaretRight,
  PiFile,
  PiFileDoc,
  PiFileImage,
  PiFilePdf,
  PiFilePpt,
  PiFileXls,
  PiFolder,
  PiLockSimple,
  PiSignIn,
  PiUserSwitch,
} from 'react-icons/pi'

type FileListProps = {
  layout: Layout
  fileBrowser: UseAssetFileBrowserReturn
  getDownloadLinkHref: (id: number) => string
  /** Opens the page's Account drawer; offered when protection may hide files. */
  onSignIn: () => void
}

/** One row per file or folder, in the asset frontend's design language. */
const ROW =
  'rda-tile rda-edge flex w-full items-center gap-3 bg-[var(--rda-surface)] px-4 py-3 ' +
  'text-start text-[var(--rda-on-surface)]'

// Shared with the classic design (AssetViewClassic renders FileComponent -> FileList), so
// the loading spinner follows the asset-frontend switch; the rest of this file was already
// redesigned unconditionally by an earlier batch.
const FileList = (p: FileListProps) => {
  const newDesign = useAssetFrontendDesign()

  if (p.fileBrowser.isLoading) {
    return newDesign ? <RdaLoading /> : <Loading loading={true} />
  }

  if (p.fileBrowser.tree == null) {
    return null
  }

  const items = p.fileBrowser.currentTreeLevel
  const folderCount = items.filter((m) => m.meta == null).length
  const fileCount = items.length - folderCount

  return (
    <div className="flex flex-col gap-px">
      {p.fileBrowser.breadcrumb.length > 0 && (
        <button type="button" className={ROW} onClick={() => p.fileBrowser.back()}>
          <PiArrowLeft aria-hidden className="shrink-0 text-xl" />
          <span className="flex-1 text-sm font-medium leading-tight tracking-[-0.01em]">Back</span>
        </button>
      )}

      {/* What is in this level: a count of folders and files, so an empty
          component is distinguishable from one that has content before scanning
          the rows. */}
      {items.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--rda-muted)]">
          {folderCount > 0 && (
            <span className="flex items-center gap-1.5">
              <PiFolder aria-hidden className="text-base" />
              <span className="tabular-nums">{folderCount}</span>
              <span>{folderCount === 1 ? 'folder' : 'folders'}</span>
            </span>
          )}
          {fileCount > 0 && (
            <span className="flex items-center gap-1.5">
              <PiFile aria-hidden className="text-base" />
              <span className="tabular-nums">{fileCount}</span>
              <span>{fileCount === 1 ? 'file' : 'files'}</span>
            </span>
          )}
        </div>
      )}

      {items.length === 0 &&
        (p.fileBrowser.hiddenByProtection ? (
          <ProtectedEmptyState onSignIn={p.onSignIn} />
        ) : (
          <p className="px-4 py-10 text-center text-sm text-[var(--rda-muted)]">
            No files or folders here
          </p>
        ))}

      {items.map((m) => {
        const icon = m.meta == null ? <PiFolder /> : <FileIcon mediaType={m.meta?.mediaType ?? null} />
        const label = (
          <span className="flex-1 text-sm font-medium leading-tight tracking-[-0.01em]">
            {m.name}
          </span>
        )
        const chevron = (
          <PiCaretRight aria-hidden className="h-4 w-4 shrink-0 opacity-60 rtl:-scale-x-100" />
        )

        if (m.meta != null) {
          return (
            <a
              key={m.meta.id}
              className={ROW}
              href={p.getDownloadLinkHref(m.meta.id)}
              rel="noreferrer"
              target="_blank"
            >
              <span aria-hidden className="shrink-0 text-xl">
                {icon}
              </span>
              {label}
              {chevron}
            </a>
          )
        }

        return (
          <button
            key={p.fileBrowser.breadcrumb + '_' + m.name}
            type="button"
            className={ROW}
            onClick={() => p.fileBrowser.select(m.name)}
          >
            <span aria-hidden className="shrink-0 text-xl">
              {icon}
            </span>
            {label}
            {chevron}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The list is empty, but asset or folder protection hid something from this
 * visitor. The server never says what or how much, so the copy says "may".
 * Same lock mark and actions as the product's sign-in-required screen
 * (AssetProtectedSignIn), sized down to sit inside the file drawer.
 */
function ProtectedEmptyState({ onSignIn }: { onSignIn: () => void }) {
  const { t } = useTranslation()
  const user = useUiContext().assetFrontendAuthedUser
  const unverified = user?.emailUnverified != null

  return (
    <div className="flex flex-col items-center px-4 py-10 text-center">
      <span
        aria-hidden
        className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--rda-accent-tint)] text-[var(--rda-accent-text)]"
      >
        <PiLockSimple className="h-5 w-5" />
      </span>
      <p className="mt-3 text-sm text-[var(--rda-muted)]">No files or folders here</p>
      <p className="mt-1.5 max-w-[36ch] text-[15px] leading-snug text-[var(--rda-on-surface)] [text-wrap:balance]">
        {user == null ? t('assetView.files.protectedHint') : t('assetView.files.protectedNoAccess')}
      </p>
      {user != null && unverified ? (
        <p
          className="mt-3 max-w-[40ch] rounded-lg bg-[var(--rda-neutral)] px-3 py-2.5 text-start text-sm leading-snug text-[var(--rda-on-neutral)]"
          role="note"
        >
          {t('assetView.protected.verifyEmail')}
        </p>
      ) : null}
      {user == null ? (
        <PrimaryButton className="mt-5 min-h-12 max-w-xs rounded-lg sm:rounded-xl" type="button" onClick={onSignIn}>
          <PiSignIn aria-hidden className="h-5 w-5" />
          {t('assetView.protected.signIn')}
        </PrimaryButton>
      ) : (
        <SecondaryButton
          className="mt-5 min-h-12 w-full max-w-xs rounded-lg sm:rounded-xl"
          type="button"
          onClick={onSignIn}
        >
          <PiUserSwitch aria-hidden className="h-5 w-5" />
          {t('assetView.protected.switchAccount')}
        </SecondaryButton>
      )}
    </div>
  )
}

function FileIcon({ mediaType }: { mediaType: string | null }) {
  switch (mediaType) {
    case 'text/plain':
      return <PiBookOpen />
    case 'application/pdf':
      return <PiFilePdf />
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'doc':
      return <PiFileDoc />
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return <PiFileXls />
    case 'image/ppt':
      return <PiFilePpt />
    case 'image/jpg':
    case 'image/jpeg':
    case 'image/png':
      return <PiFileImage />
    default:
      return <PiFile />
  }
}

export default FileList

